// ABOUTME: Card component with optional image and href support
// ABOUTME: Provides Flowbite-compatible API for content cards

import * as React from "react"
import { cn } from "@shared/lib/utils"

// Base Card component
const CardBase = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("rounded-xl bg-white text-gray-900", className)}
      {...props}
    />
  ),
)
CardBase.displayName = "CardBase"

// Card subcomponents
const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-1.5 p-6", className)}
      {...props}
    />
  ),
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  ),
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("text-sm text-gray-500", className)}
      {...props}
    />
  ),
)
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("p-6 pt-0", className)}
      {...props}
    />
  ),
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center p-6 pt-0", className)}
      {...props}
    />
  ),
)
CardFooter.displayName = "CardFooter"

// Main CCard component with Flowbite-compatible API
export interface CCardProps extends React.HTMLAttributes<HTMLDivElement> {
  href?: string
  imgAlt?: string
  imgSrc?: string
  horizontal?: boolean
  fullSize?: boolean
  /** Card variant: "white" (default) or "gray" for form sections */
  variant?: "white" | "gray"
  /** Override default padding */
  noPadding?: boolean
}

export const CCard = React.forwardRef<HTMLDivElement, CCardProps>(
  (
    { className, href, imgAlt, imgSrc, horizontal, fullSize, variant = "white", noPadding, children, ...props },
    ref,
  ) => {
    const variantClasses = {
      white: "bg-white",
      gray: "bg-customGray-100",
    }

    const content = (
      <CardBase
        ref={ref}
        className={cn(
          variantClasses[variant],
          horizontal && "flex-row",
          fullSize && "w-full min-h-full flex flex-col",
          className,
        )}
        {...props}
      >
        {imgSrc && (
          <img
            src={imgSrc}
            alt={imgAlt || ""}
            className={cn(horizontal ? "w-48 h-full object-cover rounded-l-xl" : "w-full rounded-t-xl")}
          />
        )}
        <div className={cn(!noPadding && "p-5", fullSize && "grow flex flex-col")}>{children}</div>
      </CardBase>
    )

    if (href) {
      return (
        <a
          href={href}
          className="block"
        >
          {content}
        </a>
      )
    }

    return content
  },
)

CCard.displayName = "CCard"

// Export subcomponents for compound usage
export { CardHeader, CardTitle, CardDescription, CardContent, CardFooter }
